/**
 * Transport-level contract for the default-namespace ProgressGateway.
 *
 * These types describe the socket connection itself rather than any one feature's
 * events. They exist because the client can no longer fall back to polling: with
 * polling in place a socket that silently failed to join a room was merely slow,
 * and now it is permanently wrong. The gateway has to say what it actually did.
 */

import type { DeckStatus, ReportStatus } from "./reporting/contracts.generated";

/**
 * What happened when the gateway tried to put this socket into
 * `organization:{token}` after resolving the user's org from the database.
 *
 * - `joined`   — in the room; org-scoped events will arrive.
 * - `none`     — the user genuinely has no organization. Not an error, but
 *                org-scoped events will never arrive and the client should not
 *                wait on them.
 * - `failed`   — the lookup threw. Same observable outcome as `none`, opposite
 *                meaning: this is a fault and the client should degrade.
 */
export type OrganizationRoomOutcome = "joined" | "none" | "failed";

/**
 * Whether broadcasts originating on *other* backend pods can reach this socket.
 *
 * The Redis adapter is attached asynchronously and its failure is non-fatal, so a
 * pod can serve sockets from its in-memory adapter indefinitely. Such a socket
 * receives events emitted by its own pod and silently misses every other one.
 */
export type CrossInstanceDeliveryState = "ok" | "degraded" | "pending";

/**
 * Emitted once per connection on `rooms:status`, after the org-room lookup settles.
 *
 * `degraded: true` means at least one class of server-pushed event cannot reach
 * this socket. Clients should treat silence as unreliable while degraded — widen
 * the stall watchdog, revalidate on focus, or surface a warning — instead of
 * assuming no event means no change.
 */
export interface RoomsStatusEvent {
  /** Whether the socket joined `user:{userId}`. */
  userRoom: boolean;
  organizationRoom: OrganizationRoomOutcome;
  crossInstanceDelivery: CrossInstanceDeliveryState;
  degraded: boolean;
  timestamp: string;
}

/** Event name carrying {@link RoomsStatusEvent}. */
export const ROOMS_STATUS_EVENT = "rooms:status";

/**
 * Lightweight ping to `user:{userId}` when the resumable latest import session
 * may have changed. Carries no snapshot — clients refetch `GET …/latest`.
 */
export interface UserImportSessionLatestChangedEvent {
  sessionId: string;
  reason: string;
  timestamp: string;
}

export const USER_IMPORT_SESSION_LATEST_CHANGED_EVENT =
  "user:import-session-latest-changed";

export interface ExperimentReadinessChangedEvent {
  experimentId: string;
  /** Generated from `vocab.reporting.report_status`. */
  reportStatus: ReportStatus | null;
  /** Generated from `vocab.reporting.deck_status`. */
  deckStatus: DeckStatus | null;
  deckJsonKey: string | null;
  timestamp: string;
}

export const EXPERIMENT_READINESS_CHANGED_EVENT =
  "notification:experiment_readiness_changed";

export const EXPERIMENT_ROOM_READINESS_EVENT = "experiment:readiness";

/**
 * The report outcome values `experiment:{id}:status` carries after a report
 * decision; the same channel also carries experiment statuses.
 * `deck_failed` only when the deck itself failed; `report_failed` when the
 * report failed with its deck readable or not expected (a failed Summary).
 */
export type ReportStatusEvent =
  | "deck_processing"
  | "deck_failed"
  | "report_failed"
  | "report_ready";

/** Revision ping for a public study share — no report payload on the wire. */
export interface PublicShareRevisionEvent {
  shareToken: string;
  reason: string;
  timestamp: string;
}

export const PUBLIC_SHARE_REVISION_EVENT = "public-share:revision";

/** Lightweight invalidation when a thread message is persisted server-side. */
export interface KnowledgeThreadMessageAddedEvent {
  threadToken: string;
  askId: string | null;
  timestamp: string;
}

export const KNOWLEDGE_THREAD_MESSAGE_ADDED_EVENT =
  "knowledge-thread:message-added";
